import { useDatabaseContent } from '@databases/database-state';
import { isEmbed, isNativeEmbedEditorPlugin } from '@databases/modes';
import { requestResize } from '@databases/native-embed-editor-plugin';
import usePanelState from '@databases/panel/usePanelState';
import React, { type ReactNode, useEffect } from 'react';

import { getExperimentValue } from '@canvas/client-feature-flags';

function isNativeEmbedsV2Enabled(): boolean {
  return (
    getExperimentValue('mauiExperiment', 'isEnabled', {
      fireExperimentExposure: false
    }) &&
    getExperimentValue('dbNativeEmbedsV2', 'isEnabled', {
      fireExperimentExposure: false
    })
  );
}

const NATIVE_EMBED_MAX_HEIGHT = 720;
const NATIVE_EMBED_WIDTH_LARGE = 1200;
const NATIVE_EMBED_WIDTH_DEFAULT = 760;
const RESIZE_DEBOUNCE_MS = 200;

const EMBED_MAX_HEIGHT = 700;
const EMBED_PANEL_MIN_HEIGHT = 560;
const EMBED_ENTRY_MIN_HEIGHT = 100;
const EMBED_DEFAULT_MIN_HEIGHT = 400;
const EMBED_EXTRA_PADDING = 30;

// Fallback when toolbar is not yet in the DOM (e.g. Suspense not yet resolved).
const ENTRY_VIEW_TOOLBAR_FALLBACK = 30;

function calculateNativeEmbedWidth(contentWidth: number): number {
  return contentWidth >= NATIVE_EMBED_WIDTH_LARGE
    ? NATIVE_EMBED_WIDTH_LARGE
    : NATIVE_EMBED_WIDTH_DEFAULT;
}

function getToolbarHeight(): number {
  const toolbarEl = document.getElementById('layout-toolbar-wrapper');
  if (!toolbarEl) {
    return ENTRY_VIEW_TOOLBAR_FALLBACK;
  }

  const toolbarHeight = toolbarEl.getBoundingClientRect().height;

  // The ViewWrapper for table layout has a -8px margin-top to offset column drag handle
  // padding, which means it visually overlaps the toolbar by 8px. Subtract that overlap
  // so we don't over-report the total height.
  const viewWrapperEl = document.querySelector<HTMLElement>(
    '[data-testid="layout-view--wrapper"]'
  );
  const viewWrapperMarginTop = viewWrapperEl
    ? Math.abs(parseInt(getComputedStyle(viewWrapperEl).marginTop, 10)) || 0
    : 0;

  const entryTitleEl = document.getElementById('layout-entry-title-wrapper');
  const entryTitleHeight = entryTitleEl ? entryTitleEl.getBoundingClientRect().height : 0;

  return toolbarHeight - viewWrapperMarginTop + entryTitleHeight;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function getNativeEmbedContentMaxHeight(contentHeight: number): number {
  return clamp(contentHeight, 1, NATIVE_EMBED_MAX_HEIGHT);
}

/**
 * Non-referencing (table view): #resizeObserver is inline-block so its offsetHeight
 * shrink-wraps its content — it correctly decreases when rows are deleted.
 * Skip getToolbarHeight() when the toolbar isn't in the DOM to avoid the 30px fallback.
 */
function getNonRefTotalHeight(observedElement: HTMLElement): number {
  const toolbarEl = document.getElementById('layout-toolbar-wrapper');
  return observedElement.offsetHeight + (toolbarEl ? getToolbarHeight() : 0);
}

/**
 * Referencing (entry view): firstElementChild (DatabaseEntry root) has natural content
 * height unaffected by flex-stretch. getBoundingClientRect avoids offsetHeight being
 * clipped by ancestor overflow containers.
 */
function getRefTotalHeight(contentEl: HTMLElement): number {
  return contentEl.getBoundingClientRect().height + getToolbarHeight();
}

// ---------------------------------------------------------------------------
// Path 1 — Exp ON, Non-referencing (table view)
// Debounce and keep observing so row changes continue to update maxHeight.
// ---------------------------------------------------------------------------
function observeNativeEmbedExpOnNonRef(observedElement: HTMLElement): () => void {
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let lastMaxHeight: number | null = null;
  let lastWidth: number | null = null;

  const observer = new window.ResizeObserver(() => {
    if (debounceTimer !== null) {
      clearTimeout(debounceTimer);
    }
    debounceTimer = setTimeout(() => {
      const toolbarEl = document.getElementById('layout-toolbar-wrapper');
      // Wait for toolbar to be in the DOM and have real height — it loads via Suspense
      // and starts at 0. Without the toolbar, the 30px fallback would lock in incorrectly.
      // Start observing it now so when it resolves the callback re-fires.
      if (!toolbarEl || toolbarEl.getBoundingClientRect().height === 0) {
        if (toolbarEl) {
          observer.observe(toolbarEl);
        }
        return;
      }
      const maxHeight = getNativeEmbedContentMaxHeight(
        getNonRefTotalHeight(observedElement)
      );
      const width = calculateNativeEmbedWidth(observedElement.offsetWidth);
      if (lastMaxHeight === maxHeight && lastWidth === width) {
        return;
      }

      lastMaxHeight = maxHeight;
      lastWidth = width;
      requestResize(width, maxHeight, maxHeight);
    }, RESIZE_DEBOUNCE_MS);
  });

  // #resizeObserver is inline-block so its offsetHeight shrinks with content.
  // Also observe the toolbar so the callback re-fires when Suspense resolves.
  observer.observe(observedElement);
  const toolbarEl = document.getElementById('layout-toolbar-wrapper');
  if (toolbarEl) {
    observer.observe(toolbarEl);
  }

  return () => {
    observer.disconnect();
    if (debounceTimer !== null) {
      clearTimeout(debounceTimer);
    }
  };
}

// ---------------------------------------------------------------------------
// Path 2 — Exp ON, Referencing (entry view)
// Debounce → fire once → disconnect.
// ---------------------------------------------------------------------------
function observeNativeEmbedExpOnRef(observedElement: HTMLElement): () => void {
  const contentEl =
    (observedElement.firstElementChild as HTMLElement | null) ?? observedElement;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;

  const observer = new window.ResizeObserver(() => {
    if (debounceTimer !== null) {
      clearTimeout(debounceTimer);
    }
    debounceTimer = setTimeout(() => {
      const toolbarEl = document.getElementById('layout-toolbar-wrapper');
      // Wait for toolbar to be in the DOM and have real height — it loads via Suspense
      // and starts at 0. Without the toolbar, the 30px fallback would lock in incorrectly.
      // Start observing it now so when it resolves the callback re-fires.
      if (!toolbarEl || toolbarEl.getBoundingClientRect().height === 0) {
        if (toolbarEl) {
          observer.observe(toolbarEl);
        }
        return;
      }
      const maxHeight = getNativeEmbedContentMaxHeight(getRefTotalHeight(contentEl));
      const width = calculateNativeEmbedWidth(observedElement.offsetWidth);
      observer.disconnect();
      requestResize(width, maxHeight, maxHeight);
    }, RESIZE_DEBOUNCE_MS);
  });

  // Observe contentEl (DatabaseEntry root) — natural content height unaffected by flex-stretch.
  // Also observe toolbar so the callback re-fires when Suspense resolves.
  observer.observe(contentEl);
  const toolbarEl = document.getElementById('layout-toolbar-wrapper');
  if (toolbarEl) {
    observer.observe(toolbarEl);
  }

  return () => {
    observer.disconnect();
    if (debounceTimer !== null) {
      clearTimeout(debounceTimer);
    }
  };
}

// ---------------------------------------------------------------------------
// Exp OFF — Non-referencing and Referencing
// Height fires immediately on every change; width debounces once then fixed.
// Never disconnects — content changes must keep updating height.
//
// Non-ref: observes observedElement directly (inline-block, offsetHeight shrinks
//   with content when rows are deleted) and uses getNonRefTotalHeight.
// Ref: observes firstElementChild (DatabaseEntry root, natural content height
//   unaffected by flex-stretch) and uses getRefTotalHeight.
// ---------------------------------------------------------------------------
function observeNativeEmbedExpOff(
  observedElement: HTMLElement,
  referencing: boolean
): () => void {
  const contentEl =
    (observedElement.firstElementChild as HTMLElement | null) ?? observedElement;
  const getHeight = referencing
    ? () => getRefTotalHeight(contentEl)
    : () => getNonRefTotalHeight(observedElement);
  const observeTarget = referencing ? contentEl : observedElement;

  let lastHeight: number | null = null;
  let fixedWidth: number | null = null;
  let widthDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  const observer = new window.ResizeObserver(() => {
    const height = getNativeEmbedContentMaxHeight(getHeight());

    if (fixedWidth === null) {
      if (widthDebounceTimer !== null) {
        clearTimeout(widthDebounceTimer);
      }
      widthDebounceTimer = setTimeout(() => {
        fixedWidth = calculateNativeEmbedWidth(observedElement.offsetWidth);
        requestResize(fixedWidth, lastHeight ?? 1, lastHeight ?? 1);
      }, RESIZE_DEBOUNCE_MS);
    }

    if (lastHeight === height) {
      return;
    }
    lastHeight = height;
    requestResize(fixedWidth ?? undefined, height, height);
  });

  observer.observe(observeTarget);
  const toolbarEl = document.getElementById('layout-toolbar-wrapper');
  if (toolbarEl) {
    observer.observe(toolbarEl);
  }

  return () => {
    observer.disconnect();
    if (widthDebounceTimer !== null) {
      clearTimeout(widthDebounceTimer);
    }
  };
}

function observeNativeEmbedResize(
  observedElement: HTMLElement,
  referencing: boolean
): () => void {
  if (isNativeEmbedsV2Enabled()) {
    return referencing
      ? observeNativeEmbedExpOnRef(observedElement)
      : observeNativeEmbedExpOnNonRef(observedElement);
  }
  return observeNativeEmbedExpOff(observedElement, referencing);
}

function observeEmbedResize(
  observedElement: HTMLElement,
  isPanelVisible: () => boolean,
  entryId: string | null
): () => void {
  const minHeight = isPanelVisible()
    ? EMBED_PANEL_MIN_HEIGHT
    : entryId
      ? EMBED_ENTRY_MIN_HEIGHT
      : EMBED_DEFAULT_MIN_HEIGHT;

  const observer = new window.ResizeObserver(() => {
    const newHeight = clamp(
      observedElement.offsetHeight + getToolbarHeight() + EMBED_EXTRA_PADDING,
      minHeight,
      EMBED_MAX_HEIGHT
    );
    window.parent.postMessage(
      JSON.stringify({ method: 'resize', height: Math.max(newHeight, 1) }),
      '*'
    );
  });

  observer.observe(observedElement);
  return () => observer.disconnect();
}

const ResizeObserver: React.FC<{
  children?: ReactNode | undefined;
  disableResize?: boolean;
}> = ({ children, disableResize = false }) => {
  const { entryId, referencing } = useDatabaseContent();
  const ref = React.useRef<HTMLDivElement>(null);
  const { isPanelVisible } = usePanelState();

  useEffect(() => {
    const observedElement = ref.current;
    if (!observedElement || disableResize) {
      return;
    }

    if (isNativeEmbedEditorPlugin()) {
      return observeNativeEmbedResize(observedElement, referencing);
    }

    // Skip resize in EMBED+referencing mode — the parent picker frame doesn't handle it.
    if (isEmbed() && !referencing) {
      return observeEmbedResize(observedElement, isPanelVisible, entryId);
    }
  }, [isPanelVisible, entryId, disableResize, referencing]);

  return (
    <div
      ref={ref}
      id="resizeObserver"
      style={{
        // inline-block prevents inheriting height:100% from ancestor containers,
        // allowing the div to shrink-wrap its content for accurate offsetHeight reads.
        display: isNativeEmbedEditorPlugin() ? 'inline-block' : 'block',
        width: '100%'
      }}
    >
      {children}
    </div>
  );
};

export default ResizeObserver;
